import { weekFileName } from '../lib/weekFileName';
import { DocLangToggle } from './ui/doc-lang-toggle';
import { useTranslation } from 'react-i18next';
/**
 * SurveysList — internal list of customer satisfaction surveys.
 *
 * Reused in two places:
 *   1. Inside ProjectsTab edit dialog → pass `projectId` to filter.
 *   2. As a sub-tab inside the ISO 9001 module → no filter, shows all.
 *
 * Server-side paginated via the `X-Total-Count` header convention.
 * Internal users can: create, copy the public share URL, view
 * progress, approve completed surveys, toggle anonymisation, delete.
 */
import { weekToRange } from '../lib/weekFilter';
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from './ui/dialog';
import {
  Plus, Trash2, Link as LinkIcon, CheckCircle2, MessageSquare, Eye,
  FileDown, Settings as SettingsIcon,
} from 'lucide-react';
import PaginationFooter from './PaginationFooter';
import SurveyTemplatesEditor from './SurveyTemplatesEditor';
import { SearchSortBar, CREATED_SORT_OPTIONS } from './ListSearchSort';
import { useSavedView } from '../hooks/useSavedView';
import { FloatingSaveButton } from './FloatingSaveButton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_BADGE = {
  draft: 'bg-slate-100 text-slate-700',
  sent: 'bg-blue-100 text-blue-700',
  customer_completed: 'bg-amber-100 text-amber-700',
  approved: 'bg-green-100 text-green-700',
  archived: 'bg-gray-100 text-gray-500',
};

const STATUS_LABEL = {
  draft: 'Draft',
  sent: 'Sent to customer',
  customer_completed: 'Customer completed',
  approved: 'Approved',
  archived: 'Archived',
};

export default function SurveysList({ projectId, projects: projectsProp }) {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [templates, setTemplates] = useState([]);
  const [projects, setProjects] = useState(projectsProp || []);
  const [pager, setPager] = useState({ skip: 0, limit: 25 });
  const [createOpen, setCreateOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const { search, setSearch, sortBy, setSortBy, viewLoaded } = useSavedView('surveys');
  const [week, setWeek] = useState(null);

  // Search and sort are resolved server-side so they cover every page,
  // not just the rows currently loaded. Debounced so typing doesn't
  // fire a request per keystroke.
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  const fetchList = async () => {
    const params = new URLSearchParams();
    params.set('skip', pager.skip);
    params.set('limit', pager.limit);
    params.set('sort_by', sortBy);
    if (projectId) params.set('project_id', projectId);
    if (debouncedSearch.trim()) params.set('q', debouncedSearch.trim());
    if (week) {
      const r0 = weekToRange(week);
      params.set('date_from', r0.from);
      params.set('date_to', r0.to);
    }
    const r = await axios.get(`${API}/customer-surveys?${params.toString()}`);
    setItems(r.data || []);
    setTotal(parseInt(r.headers['x-total-count'] || '0', 10));
  };

  // A new search means the old page offset is meaningless.
  useEffect(() => {
    if (!viewLoaded) return;
    setPager((p) => (p.skip === 0 ? p : { ...p, skip: 0 }));
  }, [debouncedSearch, sortBy, viewLoaded, week]);

  useEffect(() => {
    if (!viewLoaded) return;
    fetchList();
    /* eslint-disable-next-line */
  }, [pager.skip, pager.limit, projectId, debouncedSearch, sortBy, viewLoaded, week]);

  useEffect(() => {
    axios.get(`${API}/customer-survey-templates`).then((r) => setTemplates(r.data || []));
    if (!projectsProp) {
      axios.get(`${API}/projects?limit=2000`).then((r) => setProjects(r.data || []));
    }
  }, [projectsProp]);

  const projectName = (pid) => projects.find((p) => p.id === pid)?.name || '—';

  const copyShareLink = async (s) => {
    const url = `${window.location.origin}/survey/${s.share_token}`;
    try {
      await navigator.clipboard.writeText(url);
      // After copy, mark as 'sent' so the admin sees movement in the
      // list. Safe to call multiple times (it no-ops if already sent).
      if (s.status === 'draft') {
        await axios.post(`${API}/customer-surveys/${s.id}/mark-sent`);
        fetchList();
      }
      toast.success(t('auto.misc.surveyLinkCopiedToClipboard'));
    } catch (e) {
      // Some browsers/contexts block clipboard.writeText → fall back to
      // a textarea select trick so the admin can copy manually.
      window.prompt('Copy this link to share with the customer:', url);
    }
  };

  const handleApprove = async (sid) => {
    if (!window.confirm(t('auto.misc.approveThisSurveyItWillBe'))) return;
    try {
      await axios.post(`${API}/customer-surveys/${sid}/approve`);
      toast.success(t('auto.misc.surveyApproved'));
      fetchList();
    } catch (e) { toast.error(e.response?.data?.detail || 'Approve failed'); }
  };

  const handleDelete = async (sid) => {
    if (!window.confirm(t('auto.misc.deleteThisSurveyApprovedSurveysWill'))) return;
    try {
      await axios.delete(`${API}/customer-surveys/${sid}`);
      toast.success(t('auto.misc.removed'));
      fetchList();
    } catch (e) { toast.error(t('auto.misc.deleteFailed')); }
  };

  const downloadPdf = async (s) => {
    try {
      const r = await axios.get(`${API}/customer-surveys/${s.id}/pdf`, { responseType: 'blob' });
      // Pull filename from Content-Disposition (server is the canonical source).
      const cd = r.headers['content-disposition'] || '';
      const match = cd.match(/filename="?([^";]+)"?/);
      const fname = match ? match[1] : `survey-${s.id.slice(0, 8)}.pdf`;
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = weekFileName(fname); a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'PDF download failed');
    }
  };

  const [templatesOpen, setTemplatesOpen] = useState(false);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5" /> {t('auto.misc.customerSatisfactionSurveys')}
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={() => setTemplatesOpen(true)}
              data-testid="survey-templates-btn" title={t('auto.misc.editSurveyTemplates')}>
              <SettingsIcon className="h-4 w-4 mr-1" /> {t('auto.misc.templates')}
            </Button>
            <Button size="sm" onClick={() => setCreateOpen(true)} data-testid="survey-new-btn">
              <Plus className="h-4 w-4 mr-1" /> {t('auto.misc.newSurvey')}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <SearchSortBar
          search={search}
          onSearch={setSearch}
          sortBy={sortBy}
          onSort={setSortBy}
          placeholder={t('auto.misc.searchAllPagesByProjectScope')}
          testIdPrefix="surveys"
          week={week}
          onWeek={setWeek}
          className="mb-3"
          options={[
            ...CREATED_SORT_OPTIONS,
            { value: 'project', label: t('auto.lbl.project') },
            { value: 'status', label: t('auto.lbl.status') },
            { value: 'contact', label: t('auto.lbl.customerContact') },
          ]}
        />
        {items.length === 0 ? (
          <div className="text-sm text-slate-400 italic py-6 text-center">
            {debouncedSearch.trim()
              ? `No surveys match “${debouncedSearch.trim()}”.`
              : 'No surveys yet.'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600 text-xs">
                <tr>
                  <th className="text-left px-2 py-2">{t('auto.misc.project')}</th>
                  <th className="text-left px-2 py-2">{t('auto.misc.scope')}</th>
                  <th className="text-left px-2 py-2">{t('auto.misc.customerContact')}</th>
                  <th className="text-left px-2 py-2">{t('auto.misc.status')}</th>
                  <th className="text-right px-2 py-2 w-44">{t('auto.misc.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((s) => (
                  <tr key={s.id} className="border-t hover:bg-slate-50" data-testid={`survey-row-${s.id}`}>
                    <td className="px-2 py-2 truncate max-w-[200px]">{projectName(s.project_id)}</td>
                    <td className="px-2 py-2 truncate max-w-[180px] text-slate-500">
                      {s.scope_description || <span className="italic">{t('auto.misc.wholeProject')}</span>}
                      {s.anonymisation?.enabled && (
                        <Badge variant="outline" className="ml-2 text-[10px] bg-purple-50 text-purple-700 border-purple-200">anonymised</Badge>
                      )}
                    </td>
                    <td className="px-2 py-2 truncate max-w-[160px]">{s.customer_contact_name || '—'}</td>
                    <td className="px-2 py-2">
                      <Badge className={`${STATUS_BADGE[s.status] || ''} text-[10px]`}>
                        {STATUS_LABEL[s.status] || s.status}
                      </Badge>
                      {s.customer_signed_name && (
                        <span className="block text-[10px] text-slate-400 mt-0.5">
                          signed by {s.customer_signed_name}
                        </span>
                      )}
                    </td>
                    <td className="px-2 py-2 text-right whitespace-nowrap">
                      <Button size="icon" variant="ghost" className="h-7 w-7" title={t('auto.misc.viewEdit')}
                        onClick={() => setEditingId(s.id)} data-testid={`survey-edit-${s.id}`}>
                        <Eye className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="icon" variant="ghost" className="h-7 w-7" title={t('auto.misc.copyShareLink')}
                        onClick={() => copyShareLink(s)} data-testid={`survey-copy-${s.id}`}>
                        <LinkIcon className="h-3.5 w-3.5 text-blue-600" />
                      </Button>
                      {s.status === 'customer_completed' && (
                        <Button size="icon" variant="ghost" className="h-7 w-7" title={t('auto.misc.approve')}
                          onClick={() => handleApprove(s.id)} data-testid={`survey-approve-${s.id}`}>
                          <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
                        </Button>
                      )}
                      <DocLangToggle testId={`doc-lang-surveyslist-0-${s.id}`} />
                      {['customer_completed', 'approved', 'archived'].includes(s.status) && (
                        <Button size="icon" variant="ghost" className="h-7 w-7" title={t('auto.misc.downloadPdf')}
                          onClick={() => downloadPdf(s)} data-testid={`survey-pdf-${s.id}`}>
                          <FileDown className="h-3.5 w-3.5 text-indigo-600" />
                        </Button>
                      )}
                      <Button size="icon" variant="ghost" className="h-7 w-7" title={t('auto.misc.deleteArchive')}
                        onClick={() => handleDelete(s.id)} data-testid={`survey-del-${s.id}`}>
                        <Trash2 className="h-3.5 w-3.5 text-red-500" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <PaginationFooter
          total={total}
          skip={pager.skip}
          limit={pager.limit}
          onChange={setPager}
          testIdPrefix="surveys-pagination"
        />
      </CardContent>

      {createOpen && (
        <SurveyCreateDialog
          defaultProjectId={projectId}
          templates={templates}
          projects={projects}
          onClose={() => setCreateOpen(false)}
          onSaved={() => { setCreateOpen(false); fetchList(); }}
        />
      )}
      {editingId && (
        <SurveyEditDialog
          sid={editingId}
          onClose={() => setEditingId(null)}
          onSaved={() => { setEditingId(null); fetchList(); }}
        />
      )}
      {templatesOpen && (
        <SurveyTemplatesEditor
          onClose={() => setTemplatesOpen(false)}
          onChanged={() => {
            // Refresh template list & survey list (templates can affect counts/labels)
            axios.get(`${API}/customer-survey-templates`).then((r) => setTemplates(r.data || []));
          }}
        />
      )}
    </Card>
  );
}


function SurveyCreateDialog({ defaultProjectId, templates, projects, onClose, onSaved }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    project_id: defaultProjectId || '',
    template_id: templates[0]?.id || '',
    scope_description: '',
    customer_contact_name: '',
    customer_contact_email: '',
  });

  // Default the template to the system one once templates load
  useEffect(() => {
    if (!form.template_id && templates.length) {
      const sys = templates.find((t) => t.is_system) || templates[0];
      setForm((f) => ({ ...f, template_id: sys.id }));
    }
  }, [templates, form.template_id]);

  const submit = async () => {
    if (!form.project_id) { toast.error(t('auto.misc.pickAProject')); return; }
    if (!form.template_id) { toast.error(t('auto.misc.pickATemplate')); return; }
    try {
      await axios.post(`${API}/customer-surveys`, form);
      toast.success(t('auto.misc.surveyCreated'));
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Create failed'); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl" data-testid="survey-create-dialog">
        <DialogHeader>
          <DialogTitle>{t('auto.misc.newCustomerSatisfactionSurvey')}</DialogTitle>
          <DialogDescription>{t('auto.misc.theSurveyStartsInDraftOnce')}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {!defaultProjectId && (
            <div>
              <Label>{t('auto.misc.project')}</Label>
              <Select value={form.project_id} onValueChange={(v) => setForm(prev => ({ ...prev, project_id: v  }))}>
                <SelectTrigger data-testid="survey-create-project"><SelectValue placeholder={t('auto.misc.pickAProject2')} /></SelectTrigger>
                <SelectContent>
                  {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label>{t('auto.misc.surveyTemplate')}</Label>
            <Select value={form.template_id} onValueChange={(v) => setForm(prev => ({ ...prev, template_id: v  }))}>
              <SelectTrigger data-testid="survey-create-template"><SelectValue placeholder={t('auto.misc.pickATemplate2')} /></SelectTrigger>
              <SelectContent>
                {templates.map((t) => (
                  <SelectItem key={t.id} value={t.id}>
                    {t.name}{t.is_system ? ' (default)' : ''} · {t.sections.length} sections
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>{t('auto.misc.scopeOptional')}</Label>
            <Input value={form.scope_description}
              onChange={(e) => setForm(prev => ({ ...prev, scope_description: e.target.value  }))}
              placeholder="e.g. Phase 1 — Electrical installation only" />
            <p className="text-[11px] text-slate-500 mt-0.5">{t('auto.misc.useThisWhenTheSurveyCovers')}</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>{t('auto.misc.customerContactName')}</Label>
              <Input value={form.customer_contact_name}
                onChange={(e) => setForm(prev => ({ ...prev, customer_contact_name: e.target.value  }))} />
            </div>
            <div>
              <Label>{t('auto.misc.customerEmail')}</Label>
              <Input type="email" value={form.customer_contact_email}
                onChange={(e) => setForm(prev => ({ ...prev, customer_contact_email: e.target.value  }))} />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="dialog-cancel-btn">{t('auto.misc.cancel')}</Button>
          <Button onClick={submit} data-testid="survey-create-submit">{t('auto.misc.createDraft')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function SurveyEditDialog({ sid, onClose, onSaved }) {
  const { t } = useTranslation();
  const [survey, setSurvey] = useState(null);
  const [template, setTemplate] = useState(null);
  const [anon, setAnon] = useState({});

  const reload = async () => {
    const r = await axios.get(`${API}/customer-surveys/${sid}`);
    setSurvey(r.data);
    setAnon(r.data.anonymisation || { enabled: false });
    const tr = await axios.get(`${API}/customer-survey-templates`);
    const tpl = (tr.data || []).find((t) => t.id === r.data.template_id);
    setTemplate(tpl);
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [sid]);

  const saveAnon = async () => {
    try {
      await axios.put(`${API}/customer-surveys/${sid}`, { anonymisation: anon });
      toast.success(t('auto.misc.anonymisationSettingsSaved'));
      onSaved();
    } catch (e) { toast.error(t('auto.misc.saveFailed')); }
  };

  // Build a {section_key.question_key → answer} lookup for the rendered
  // read-only response table.
  const answerLookup = useMemo(() => {
    const out = {};
    (survey?.answers || []).forEach((a) => { out[`${a.section_key}.${a.question_key}`] = a; });
    return out;
  }, [survey]);

  if (!survey) {
    return (
      <Dialog open onOpenChange={onClose}>
        <DialogContent><DialogHeader><DialogTitle>{t('auto.misc.loading')}</DialogTitle></DialogHeader></DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="survey-edit-dialog">
        <DialogHeader>
          <DialogTitle>{survey.title}</DialogTitle>
          <DialogDescription>
            Status: <Badge className={`${STATUS_BADGE[survey.status]} text-[10px] ml-1`}>{STATUS_LABEL[survey.status]}</Badge>
            {survey.customer_signed_at && (
              <span className="ml-2 text-xs text-slate-500">
                Signed by <strong>{survey.customer_signed_name}</strong>{survey.customer_signed_title ? `, ${survey.customer_signed_title}` : ''} on {survey.customer_signed_at.slice(0, 10)}
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        {survey.status === 'draft' || survey.status === 'sent' ? (
          <div className="rounded border border-blue-200 bg-blue-50 p-3 text-xs text-blue-900">
            {t('auto.misc.theCustomerHasnTSubmittedAnswers')}
          </div>
        ) : (
          <Card>
            <CardHeader><CardTitle className="text-sm">{t('auto.misc.customerResponses')}</CardTitle></CardHeader>
            <CardContent>
              {(template?.sections || []).map((sec) => (
                <div key={sec.key} className="mb-4">
                  <h4 className="font-semibold text-sm text-slate-800 mb-1.5">{sec.label}</h4>
                  <table className="w-full text-xs border">
                    <tbody>
                      {sec.questions.map((q) => {
                        const a = answerLookup[`${sec.key}.${q.key}`];
                        let val = '—';
                        if (a) {
                          if (q.type === 'rating') val = a.rating ? `${'★'.repeat(a.rating)}${'☆'.repeat(5 - a.rating)} (${a.rating}/5)` : '—';
                          else if (q.type === 'yes_no') val = a.yes_no === true ? 'Yes' : a.yes_no === false ? 'No' : '—';
                          else val = a.text || '—';
                        }
                        return (
                          <tr key={q.key} className="border-t">
                            <td className="px-2 py-1.5 text-slate-600 w-1/2">{q.label}</td>
                            <td className="px-2 py-1.5 font-medium">{val}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ))}
              {survey.customer_signature_image && (
                <div className="mt-4 border-t pt-3">
                  <Label className="text-xs">{t('auto.misc.customerSignature')}</Label>
                  <img src={survey.customer_signature_image} alt="signature"
                    className="mt-1 h-20 max-w-[260px] object-contain border bg-white" />
                </div>
              )}
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader><CardTitle className="text-sm">{t('auto.misc.anonymisationForPortfolioSharing')}</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            <p className="text-xs text-slate-500">{t('auto.misc.whenEnabledCustomerIdentifyingFieldsAre')}</p>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={anon.enabled || false}
                onChange={(e) => setAnon(prev => ({ ...prev, enabled: e.target.checked  }))}
                data-testid="survey-anon-enable" />
              <span className="font-medium">{t('auto.misc.treatThisSurveyAsAnonymised')}</span>
            </label>
            {anon.enabled && (
              <div className="ml-6 space-y-1 pt-1">
                {['mask_customer_name', 'mask_customer_address', 'mask_project_name', 'mask_project_location', 'mask_signatory_name']
                  .map((key) => [key, t(`surveys.anon.${key}`)]).map(([key, label]) => (
                  <label key={key} className="flex items-center gap-2 text-xs">
                    <input type="checkbox" checked={anon[key] !== false}
                      onChange={(e) => setAnon(prev => ({ ...prev, [key]: e.target.checked  }))}
                      data-testid={`survey-anon-${key}`} />
                    <span>{label}</span>
                  </label>
                ))}
                <div className="pt-1">
                  <Label className="text-xs">{t('auto.misc.sectorLabelShownInsteadOfCustomer')}</Label>
                  <Input className="h-7 text-xs" value={anon.sector_label || ''}
                    onChange={(e) => setAnon(prev => ({ ...prev, sector_label: e.target.value  }))}
                    placeholder="e.g. Logistics OEM, Pharma manufacturer" />
                </div>
              </div>
            )}
            <Button size="sm" onClick={saveAnon} className="mt-2" data-testid="survey-anon-save">
              {t('auto.misc.saveAnonymisationSettings')}
            </Button>
          </CardContent>
        </Card>

        <FloatingSaveButton
          onClick={saveAnon}
          label={t('auto.misc.saveSettings')}
          testId="survey-floating-save-btn"
        />

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="dialog-close-btn">{t('auto.misc.close')}</Button>
          <DocLangToggle testId="doc-lang-surveyslist-1" />
          {['customer_completed', 'approved', 'archived'].includes(survey.status) && (
            <Button onClick={async () => {
              try {
                const r = await axios.get(`${API}/customer-surveys/${sid}/pdf`, { responseType: 'blob' });
                const cd = r.headers['content-disposition'] || '';
                const m = cd.match(/filename="?([^";]+)"?/);
                const fname = m ? m[1] : `survey-${sid.slice(0, 8)}.pdf`;
                const url = URL.createObjectURL(r.data);
                const a = document.createElement('a');
                a.href = url; a.download = weekFileName(fname); a.click();
                URL.revokeObjectURL(url);
              } catch (e) { toast.error(t('auto.misc.pdfDownloadFailed')); }
            }} data-testid="survey-edit-pdf">
              {t('auto.misc.downloadPdf')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
